<template>
    <div class="map-wrapper" ref="mapWrapper">
        <div v-if="!props.isLocked" :id="mapId" class="map-base" />
        <div v-else class="locked-screen">
            <img class="image-fit" src="@/assets/imgs/map-placeholder.jpg" alt="forbidden-map" />
            <div class="central">
                <disabled-icon
                    class="lock-icon"
                    :access-level="props.mapAccessRequirement" />
                <div class="required-text">
                    Map requires {{ props.mapAccessRequirement ?? 'access' }}
                </div>
            </div>
        </div>
        <div v-if="props.hintText" class="hint-text">{{ props.hintText }}</div>
        <prime-button v-if="!props.isLocked" v-tooltip="`Expand map`" class="fullscreen-button" @click="toggleFullscreenMap">
            <font-awesome-icon :icon="['fas', fullscreenMapIconString]" />
        </prime-button>

        <plant-page
            v-if="selectedPlantId"
            v-model:visible="showPlantModal"
            :plant-initial-ids="[selectedPlantId]"
            :from-date="props.plantModalDateRange?.from"
            :to-date="props.plantModalDateRange?.to"
            @hide="selectedPlantId = null"
        />
    </div>
</template>

<script setup lang="ts">
import {
    onMounted, onUnmounted, ref, shallowRef, computed, watch,
} from 'vue';
import { useResizeObserver } from '@vueuse/core';
import L, { Map, GeoJSON as GeoJson } from 'leaflet';
import { Feature, GeoJsonObject } from 'geojson';
import { IExtendedLayer } from '@/interfaces/powerMarkets/maps/leaflet/IExtendedLayer.ts';
import { IFlowData, IInterconnectorFlowData } from '@/interfaces/powerMarkets/maps/interconnectors/IInterconnectorFlowData.ts';
import { IMapDataItem } from '@/interfaces/powerMarkets/maps/IMapDataItem.ts';
import { IMapZoneEvent } from '@/interfaces/powerMarkets/maps/IMapZoneEvent.ts';
import { IStyleFormat } from '@/interfaces/powerMarkets/maps/leaflet/IStyleFormat.ts';
import { IBoundaryData, IZonalDataWithObject } from '@/interfaces/powerMarkets/maps/IZonalData.ts';
import { useMapStore } from '@/stores/powerMarkets/mapStore.ts';
import ConstraintsMapLayer from '@/assets/maps/ConstraintsMapLayer.json';
import GbMapLayer from '@/assets/maps/GBMapLayer.json';
import EuMapLayer from '@/assets/maps/EUMapLayer.json';
import NetworkBoundariesMapLayer from '@/assets/maps/NetworkBoundariesMapLayer.json';
import WesternHvdcLineMapLayer from '@/assets/maps/WesternHvdcLineMapLayer.json';
import DisabledIcon from '@/components/powerMarkets/common/DisabledIcon.vue';
import 'leaflet-polylinedecorator';
import PrimeButton from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import { IDateRange } from '@/interfaces/general/IDateRange.ts';
import PlantPage from '@/components/powerMarkets/plant/PlantPage.vue';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { getConstrainedPercentageColour, getMaximumConstrainedPercentage } from '@/utils/balancing/boundaryColourHelpers.ts';

const props = withDefaults(defineProps<{
    zoom: number;
    hover: boolean;
    showPopup?: boolean;
    plantHasSize: boolean;
    plantData?: Array<IMapDataItem>;
    zoneDataOverride?: IZonalDataWithObject | null;
    boundaryDataOverride?: IBoundaryData | null;
    boundaryOption?: string | null;
    onlyBoundariesWithData?: boolean;
    interconnectorFlowData?: IInterconnectorFlowData | null;
    showInterconnectors?: boolean;
    showPlants?: boolean;
    showZones?: boolean;
    showZeroCoords?: boolean;
    hintText?: string;
    latLongCenter?: [number, number];
    zonesToHighlight?: Array<number>;
    plantsToHighlight?: Array<string>;
    showConstraintRegions?: boolean;
    showNetworkBoundaries?: boolean;
    showWesternHvdcLine?: boolean;
    countryCode?: string | null;
    isLocked?: boolean;
    mapAccessRequirement?: string | null;
    plantModalDateRange?: IDateRange;
}>(), {
    zoom: 6,
    hover: true,
    showPopup: true,
    plantHasSize: false,
    showInterconnectors: true,
    showPlants: true,
    showZones: true,
    showZeroCoords: false,
    hintText: '',
    latLongCenter: () => [54.5, -2.5],
    zonesToHighlight: () => [],
    plantsToHighlight: () => [],
    plantData: () => [],
    zoneDataOverride: null,
    boundaryDataOverride: null,
    boundaryOption: null,
    onlyBoundariesWithData: false,
    interconnectorFlowData: null,
    countryCode: null,
    isLocked: false,
    mapAccessRequirement: null,
    plantModalDateRange: undefined,
});

const emit = defineEmits<{
    'select:zone': [number];
    'select:plant': [string];
    'select:interconnector': [];
}>();

const OSM_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const DEFAULT_COLOUR = 'rgb(233, 63, 111)';
const DEFAULT_PRE_COLOUR = '#a4ce7f';
const DEFAULT_POST_COLOUR = '#00a3c7';
const MIN_PLANT_SIZE = 10;
const MAX_PLANT_SIZE = 60;

const CONSTRAINT_LINE_STYLE = {
    weight: 2,
    color: 'var(--lcpCherry)',
    opacity: 0.3,
    fillOpacity: 0.2,
    fillColor: 'var(--lcpCherry)',
    pane: 'shadowPane',
};

const NETWORK_BOUNDARY_LINE_STYLE = {
    weight: 3,
    color: 'var(--lcpLilac)',
    opacity: 0.5,
    fillOpacity: 0.5,
    fillColor: 'var(--lcpLilac)',
    pane: 'shadowPane',
};

const HVDC_LINE_STYLE = {
    weight: 4,
    color: 'var(--lcpGrapefruit)',
    opacity: 0.7,
    fillOpacity: 0.5,
    fillColor: 'var(--lcpGrapefruit)',
    pane: 'shadowPane',
};

const countryCentres: Record<string, [number, number]> = {
    AL: [41.3, 19.8],
    AD: [42.5, 1.6],
    AT: [47.5, 13.3],
    BY: [53.9, 28.0],
    BE: [50.8, 4.4],
    BA: [44.0, 17.0],
    BG: [42.7, 25.5],
    HR: [45.1, 15.2],
    CY: [35.0, 33.0],
    CZ: [49.8, 15.5],
    DK: [56.0, 10.0],
    EE: [59.0, 26.0],
    FI: [64.0, 26.0],
    FR: [46.0, 2.0],
    DE: [51.0, 10.0],
    GR: [39.0, 22.0],
    HU: [47.0, 20.0],
    IS: [64.1, -21.9],
    IE: [53.0, -8.0],
    IT: [42.5, 12.5],
    XK: [42.7, 21.1],
    LV: [56.9, 24.1],
    LI: [47.2, 9.5],
    LT: [55.0, 24.0],
    LU: [49.8, 6.1],
    MT: [35.9, 14.4],
    MD: [47.0, 29.0],
    MC: [43.7, 7.4],
    ME: [42.5, 19.3],
    NL: [52.0, 5.0],
    MK: [41.6, 21.7],
    NO: [62.0, 9.5],
    PL: [52.0, 19.0],
    PT: [39.5, -8.0],
    RO: [45.8, 24.0],
    RU: [55.0, 40.0],
    SM: [43.9, 12.5],
    RS: [44.0, 21.0],
    SK: [48.7, 19.5],
    SI: [46.1, 14.8],
    ES: [40.0, -4.0],
    SE: [62.0, 15.0],
    CH: [46.8, 8.2],
    TR: [41.0, 26.0],
    UA: [49.0, 32.0],
    GB: [54.5, -2.5],
    VA: [41.9, 12.5],
};

const mapStore = useMapStore();
const toast = useToast();

const mapId = crypto.randomUUID();

const mapWrapper = ref<HTMLElement | null>(null);
const mapIsFullscreen = ref<boolean>(false);
const mapInstance = shallowRef<Map>();
const countryJsonLayer = shallowRef<GeoJson | null>(null);
const constraintsLayer = shallowRef<GeoJson | null>(null);
const networkBoundariesLayer = shallowRef<GeoJson | null>(null);
const hvdcLinesLayer = shallowRef<GeoJson | null>(null);
const interconnectorLineLayers = shallowRef<Array<L.Polyline>>([]);
const interconnectorDecoratorLayers = shallowRef<Array<ReturnType<typeof L.polylineDecorator>>>([]);
const interconnectorMarkerLayers = shallowRef<Array<ReturnType<typeof L.circleMarker>>>([]);
const plantMarkersGroup = L.layerGroup();
const showPlantModal = ref<boolean>(false);
const selectedPlantId = ref<string | null>(null);
const dataStore = useDataStore();

useResizeObserver(mapWrapper, () => {
    mapInstance.value?.invalidateSize({ animate: false });
});

const defaultZoneStyle: IStyleFormat = {
    weight: 2,
    color: '#ECEFF1',
    opacity: 0.7,
    fillOpacity: 0.4,
};

const plantsAbsSize = computed<Array<number>>(() => plants.value.map((p) => Math.abs(p.size ?? 0)));
const maxPlantSize = computed<number>(() => Math.max(...plantsAbsSize.value));
const minPlantSize = computed<number>(() => Math.min(...plantsAbsSize.value));
const fullscreenMapIconString = computed<string>(() => (mapIsFullscreen.value ? 'compress' : 'expand'));
const isNonGbAsset = computed<boolean>(() => props.countryCode !== null && props.countryCode !== 'GB');

const plants = computed<Array<IMapDataItem>>(() => {
    const plantData = props.plantData ?? [];
    return props.showZeroCoords ? plantData : plantData.filter((plant) => plant.coords[0] !== 0 && plant.coords[1] !== 0);
});

const mapOptions = ref<{ dragging: boolean; tap: boolean }>({
    dragging: !L.Browser.mobile, tap: !L.Browser.mobile,
});

function countryFeatureFunction (feature: Feature, layer: IExtendedLayer): void {
    const colourAfter: IStyleFormat = { ...defaultZoneStyle, fillColor: '' };
    const colourBefore: IStyleFormat = { ...colourAfter };

    let bindText = isNonGbAsset.value ? `<span style='font-weight: bold'>${feature.properties?.NAME}` : `<span style='font-weight: bold'>Zone ${feature.properties?.zone}`;

    const zoneData = props.zoneDataOverride?.zonalData[feature.properties?.zone] ?? null;

    if (zoneData) {
        colourBefore.fillOpacity = 0.5;
        const zoneColour = zoneData.colour;
        colourBefore.fillColor = zoneColour;
        colourBefore.color = defaultZoneStyle.color;
        colourAfter.fillColor = zoneColour;
        colourAfter.color = '#808080';
        bindText += `:</span> ${zoneData.value} MW`;
    } else {
        colourBefore.fillOpacity = 0.5;
        colourAfter.fillOpacity = 0.5;
        let fillColor = DEFAULT_PRE_COLOUR;
        if (isNonGbAsset.value && feature.properties?.NAME !== undefined) {
            fillColor = feature.properties.ISO2 === props.countryCode ? DEFAULT_PRE_COLOUR : '';
        } else if (!isNonGbAsset.value
            && feature.properties?.zone !== undefined
            && props.zonesToHighlight.includes(feature.properties.zone)) {
            fillColor = DEFAULT_POST_COLOUR;
        }
        colourBefore.fillColor = fillColor;
        colourAfter.fillColor = DEFAULT_POST_COLOUR;
        bindText += '</span>';
    }

    layer.bindTooltip(bindText, { permanent: false, sticky: true });
    layer.setStyle(colourBefore);

    if (props.hover) {
        layer.on('mouseover', () => { layer.setStyle(colourAfter); });
        layer.on('mouseout', () => { layer.setStyle(colourBefore); });
    }
}

function getSizeFormatted (value: string): string {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function getHoverOverValue (plant: IMapDataItem): string {
    if (!plant.size) return '';
    if (plant.hoverOver) return `<br/>${plant.hoverOver}`;
    return `<br/>${getSizeFormatted(plant.size.toString())}`;
}

function getPlantDisplayValue (plantId: string): string {
    return dataStore.getPlantNameById(plantId);
}

function styleFunction (): IStyleFormat {
    return {
        ...defaultZoneStyle,
        fillColor: DEFAULT_PRE_COLOUR,
    };
}

function selectZone (event: IMapZoneEvent): void {
    const properties = event.layer.feature.properties;
    if ('zone' in properties) {
        emit('select:zone', properties.zone);
    }
}

function dynamicSize (plant: IMapDataItem): [number, number] {
    if (plant.mapSize) return plant.mapSize;
    if (plants.value.length === 1 || plant.size === null) return [10, 10];
    const minValue = minPlantSize.value;
    const thisValue = Math.abs(plant.size ?? 0);
    const radius = MIN_PLANT_SIZE + ((thisValue - minValue) / (maxPlantSize.value - minValue)) * (MAX_PLANT_SIZE - MIN_PLANT_SIZE);
    return [radius, radius];
}

function setInitialMapPosition (): void {
    const initialMap = L.map(mapId, mapOptions.value);
    let mapCentre = props.latLongCenter;
    if (props.countryCode && props.countryCode in countryCentres) {
        mapCentre = countryCentres[props.countryCode];
    }

    mapInstance.value = initialMap.setView(mapCentre, props.zoom);

    if (!mapInstance.value) return;

    L.tileLayer(OSM_URL, {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | LCP Enact | Elexon | ENTSO-E',
        minZoom: 2,
        maxZoom: 18,
        noWrap: true,
        referrerPolicy: 'strict-origin-when-cross-origin',
    }).addTo(mapInstance.value);
}

function removeCountryLayer (): void {
    removeGenericLayer(countryJsonLayer.value as L.GeoJSON);
    countryJsonLayer.value = null;
}

function toggleCountryLayer (): void {
    removeCountryLayer();

    if (!props.showZones) return;
    if (!mapInstance.value) return;

    const mapLayer = isNonGbAsset.value ? EuMapLayer : GbMapLayer;

    const countryPane = mapInstance.value.createPane('countryPane');
    countryPane.style.zIndex = '200';
    countryJsonLayer.value = L.geoJSON(mapLayer as GeoJsonObject, {
        onEachFeature: countryFeatureFunction,
        style: styleFunction(),
        pane: 'countryPane',
    }).addTo(mapInstance.value);

    if (!countryJsonLayer.value) return;
    countryJsonLayer.value.on('click', selectZone);
}

function removeConstraintsLayer (): void {
    removeGenericLayer(constraintsLayer.value as L.GeoJSON);
    constraintsLayer.value = null;
}

function toggleConstraintsLayer (): void {
    if (!props.showConstraintRegions) {
        removeConstraintsLayer();
        return;
    }

    if (!mapInstance.value) return;

    constraintsLayer.value = L.geoJSON(ConstraintsMapLayer as GeoJsonObject, {
        onEachFeature: (feature, layer) => {
            if (feature.properties && feature.properties.name) {
                const tooltip = `<span style='font-weight: bold'>${feature.properties?.name}</span>`;
                layer.bindTooltip(tooltip, { permanent: false, direction: 'top' });
            }
        },
        style: CONSTRAINT_LINE_STYLE,
    }).addTo(mapInstance.value);
}

function removeNetworkBoundariesLayer (): void {
    removeGenericLayer(networkBoundariesLayer.value as L.GeoJSON);
    networkBoundariesLayer.value = null;
}

function getBoundaryData (name: unknown): IBoundaryData['boundaryData'][string] | undefined {
    if (typeof name !== 'string') return undefined;

    const boundaryKey = name.replace(/^B/i, '');
    return props.boundaryDataOverride?.boundaryData[boundaryKey];
}

function boundaryHasRelatedConstraints (name: unknown): boolean {
    const boundaryData = getBoundaryData(name);
    return !!boundaryData && Object.keys(boundaryData.relatedConstraints).length > 0;
}

function toggleNetworkBoundariesLayer (): void {
    removeNetworkBoundariesLayer();

    if (!props.showNetworkBoundaries) {
        return;
    }

    if (!mapInstance.value) return;

    networkBoundariesLayer.value = L.geoJSON(NetworkBoundariesMapLayer as GeoJsonObject, {
        filter: feature => !props.onlyBoundariesWithData
            || boundaryHasRelatedConstraints(feature?.properties?.name),
        onEachFeature: (feature, layer) => {
            if (feature.properties && feature.properties.name) {
                const boundaryData = getBoundaryData(feature.properties.name);
                const constrainedPercentage = boundaryData
                    ? getMaximumConstrainedPercentage(boundaryData)
                    : null;
                let value = '';
                if (boundaryData) {
                    value = props.boundaryOption === 'ConstrainedPercentage'
                        ? `: ${constrainedPercentage?.toFixed(1) ?? 'N/A'}%`
                        : `: ${boundaryData.value} MW`;
                }
                const tooltip = `<span style='font-weight: bold'>${feature.properties.name}</span>${value}`;
                layer.bindTooltip(tooltip, { permanent: false, direction: 'center' });
            }
        },
        style: feature => {
            const boundaryData = getBoundaryData(feature?.properties?.name);
            return boundaryData
                ? {
                    ...NETWORK_BOUNDARY_LINE_STYLE,
                    color: props.boundaryOption === 'ConstrainedPercentage'
                        ? getConstrainedPercentageColour(getMaximumConstrainedPercentage(boundaryData))
                        : boundaryData.colour,
                }
                : NETWORK_BOUNDARY_LINE_STYLE;
        },
    }).addTo(mapInstance.value);
}

function removeHvdcLayer (): void {
    removeGenericLayer(hvdcLinesLayer.value as L.GeoJSON);
    hvdcLinesLayer.value = null;
}

function toggleHvdcLayer (): void {
    if (!props.showWesternHvdcLine) {
        removeHvdcLayer();
        return;
    }

    if (!mapInstance.value) return;

    hvdcLinesLayer.value = L.geoJSON(WesternHvdcLineMapLayer as GeoJsonObject, {
        onEachFeature: (feature, layer) => {
            if (feature.properties && feature.properties.name) {
                const tooltip = `<span style='font-weight: bold'>${feature.properties?.name}</span>`;
                layer.bindTooltip(tooltip, { permanent: false, direction: 'center' });
            }
        },
        style: HVDC_LINE_STYLE,
    }).addTo(mapInstance.value);
}

function removeGenericLayer (layerToRemove: L.Layer): void {
    if (mapInstance.value && layerToRemove) {
        if (mapInstance.value.hasLayer(layerToRemove)) {
            mapInstance.value.removeLayer(layerToRemove);
        }
    }
}

function setPlantLayers (): void {
    if (!mapInstance.value) return;

    plantMarkersGroup.clearLayers();

    if (props.showPlants) {
        const boaFlaggedColour = 'rgb(0, 47, 95)';
        const boaUnflaggedColour = 'white';
        const plantSelectedColour = 'rgb(233, 63, 111)';

        plants.value.forEach(plant => {
            const plantMarker = L.circleMarker(plant.coords, {
                color: plant.flag ? boaFlaggedColour : boaUnflaggedColour,
                fillColor: props.plantsToHighlight.includes(plant.id) ? plantSelectedColour : plant.colour,
                fillOpacity: 0.7,
                opacity: 0.7,
                weight: 2,
                radius: props.plantHasSize ? dynamicSize(plant)[0] / 2 : 10,
            });

            plantMarker.on('click', () => {
                selectPlant(plant.id);
            });

            plantMarker.bindTooltip(`<span style='font-weight: bold'>${getPlantDisplayValue(plant.id)}</span>${getHoverOverValue(plant)}`, { direction: 'top' });
            plantMarkersGroup.addLayer(plantMarker);
        });
    }

    plantMarkersGroup.addTo(mapInstance.value);
}

function removeAnyInterconnectorLayers (): void {
    if (interconnectorLineLayers.value.length > 0) {
        interconnectorLineLayers.value.forEach(line => {
            removeGenericLayer(line as L.Polyline);
        });

        interconnectorLineLayers.value = [];
    }

    if (interconnectorMarkerLayers.value.length > 0) {
        interconnectorMarkerLayers.value.forEach(marker => removeGenericLayer(marker as L.CircleMarker));

        interconnectorMarkerLayers.value = [];
    }

    if (interconnectorDecoratorLayers.value.length > 0) {
        interconnectorDecoratorLayers.value.forEach(decorator => {
            removeGenericLayer(decorator as L.LayerGroup);
        });

        interconnectorDecoratorLayers.value = [];
    }
}

function interconnectorIdToPropertyName (id: string): string {
    let propertyName = '';
    switch (id) {
        case 'InterconnectorIreland':
            propertyName = 'IntIrl';
            break;
        case 'InterconnectorEastWest':
            propertyName = 'IntEw';
            break;
        case 'InterconnectorFrance':
            propertyName = 'IntFr';
            break;
        case 'InterconnectorNetherlands':
            propertyName = 'IntNed';
            break;
        case 'InterconnectorNemo':
            propertyName = 'IntNem';
            break;
        case 'InterconnectorIfa2':
            propertyName = 'IntIfa2';
            break;
        case 'InterconnectorElecLink':
            propertyName = 'IntElec';
            break;
        case 'InterconnectorNorthSeaLink':
            propertyName = 'IntNsl';
            break;
        case 'InterconnectorVikingLink':
            propertyName = 'IntVik';
            break;
        case 'InterconnectorGreenlink':
            propertyName = 'IntGre';
            break;
    }
    return propertyName;
}

function getPolylineStyle (
    intData: IFlowData | undefined,
): L.PolylineOptions {
    if (!intData) {
        return {
            color: DEFAULT_COLOUR,
            weight: 3,
            dashArray: '4',
        };
    }

    return {
        color: intData.colour,
        weight: Math.max(intData.lineWidth, 2),
        dashArray: intData.data === 0 ? '4' : '1',
    };
}

function getInterconnectorArrowPattern (interconnectorColour: string, interconnectorValue: number): Array<{ repeat: number; symbol: typeof L.Symbol }> {
    let symbolOption;
    if (interconnectorValue > 0) {
        symbolOption = L.Symbol.arrowHead({
            pixelSize: 15,
            pathOptions: { color: interconnectorColour, weight: 1, stroke: true },
        });
    } else if (interconnectorValue < 0) {
        symbolOption = L.Symbol.arrowHead({
            pixelSize: 15,
            pathOptions: { color: interconnectorColour, weight: 1, stroke: true },
            headAngle: -290,
        });
    } else {
        symbolOption = L.Symbol.arrowHead({
            pathOptions: { color: interconnectorColour, weight: 0, stroke: true },
        });
    }
    const pattern = [
        {
            repeat: 40,
            symbol: symbolOption,
        },
    ];
    return pattern;
}

async function toggleInterconnectorLayers (): Promise<void> {
    removeAnyInterconnectorLayers();

    if (!props.showInterconnectors) {
        return;
    }

    if (!mapStore.interconnectors.length) {
        try {
            await mapStore.fetchInterconnectors();
        } catch {
            toast.add({
                summary: 'Unable to retrieve interconnectors',
                severity: 'error',
                detail: 'There was an error retrieving interconnector data',
                life: 10000,
            });
        }
    }

    mapStore.interconnectors.forEach((interconnector) => {
        const directions: Array<[number, number]> = [];
        directions.push(interconnector.originCoords);
        directions.push(interconnector.destCoords);

        const propertyName = interconnectorIdToPropertyName(interconnector.id);
        let polyline: L.Polyline;
        let markerColour = DEFAULT_COLOUR;
        let flowValue = '';

        if (propertyName && props.interconnectorFlowData) {
            const key = propertyName as keyof IInterconnectorFlowData;
            const intData = props.interconnectorFlowData[key];
            const style = getPolylineStyle(intData);
            if (intData && intData.colour) markerColour = intData.colour;
            polyline = L.polyline(directions, style).addTo(mapInstance.value as L.Map);
            if (intData) {
                const pattern = getInterconnectorArrowPattern(markerColour, intData.data);
                flowValue = `: ${intData.data} MW`;
                const decorator = L.polylineDecorator(polyline, {
                    patterns: [...pattern],
                });
                decorator.addTo(mapInstance.value as L.Map);
                decorator.bindTooltip(interconnector.name + flowValue, { direction: 'top' });
                decorator.on('click', () => {
                    emit('select:interconnector');
                });
                interconnectorDecoratorLayers.value.push(decorator);
            }
        } else {
            const style = getPolylineStyle(undefined);
            polyline = L.polyline(directions, style).addTo(mapInstance.value as L.Map);
        }

        polyline.bindTooltip(interconnector.name + flowValue, { direction: 'top' });
        polyline.on('click', () => {
            emit('select:interconnector');
        });
        interconnectorLineLayers.value.push(polyline);

        const interconnectorMarker = L.circleMarker(
            interconnector.originCoords || interconnector.destCoords,
            {
                color: markerColour,
                fillColor: markerColour,
                fillOpacity: 0.5,
                opacity: 0,
                weight: 2,
                radius: 15,
            },
        ).addTo(mapInstance.value as L.Map);

        interconnectorMarker.bindTooltip(interconnector.name + flowValue, { direction: 'top' });
        interconnectorMarker.on('click', () => {
            emit('select:interconnector');
        });
        interconnectorMarkerLayers.value.push(interconnectorMarker);
    });
}

function toggleFullscreenMap (): void {
    if (!mapIsFullscreen.value) {
        if (mapWrapper.value) {
            mapWrapper.value.requestFullscreen();
        }
    } else {
        document.exitFullscreen();
    }
}

function handleFullscreenChange (): void {
    mapIsFullscreen.value = !!document.fullscreenElement;
}

function selectPlant (plantId: string): void {
    if (props.showPopup) {
        selectedPlantId.value = plantId;
        showPlantModal.value = true;
    } else {
        emit('select:plant', plantId);
    }
}

function initialise (): void {
    setInitialMapPosition();
    setPlantLayers();
    toggleInterconnectorLayers();
    toggleHvdcLayer();
    toggleConstraintsLayer();
    toggleNetworkBoundariesLayer();
    toggleCountryLayer();
}

onMounted(async () => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    if (props.isLocked) {
        return;
    }

    if (props.showInterconnectors && !mapStore.interconnectors.length) {
        try {
            await mapStore.fetchInterconnectors();
        } catch {
            toast.add({
                summary: 'Unable to retrieve interconnectors',
                severity: 'error',
                detail: 'There was an error retrieving interconnector data',
                life: 10000,
            });
        }
    }

    initialise();
});

onUnmounted(() => {
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
    mapInstance.value?.remove();
});

watch(() => props.latLongCenter, () => {
    if (mapInstance.value && props.latLongCenter) {
        mapInstance.value.panTo(L.latLng(props.latLongCenter));
    }
});

watch(() => props.interconnectorFlowData, () => {
    toggleInterconnectorLayers();
});
watch(() => [props.plantsToHighlight, props.plantData], () => {
    setPlantLayers();
});

watch(() => [props.showWesternHvdcLine], () => {
    toggleHvdcLayer();
});

watch(() => [props.showConstraintRegions], () => {
    toggleConstraintsLayer();
});

watch(() => [props.showNetworkBoundaries, props.boundaryDataOverride, props.boundaryOption], () => {
    toggleNetworkBoundariesLayer();
});

watch(() => [props.zoneDataOverride, props.showZones, isNonGbAsset.value, props.zonesToHighlight], () => {
    toggleCountryLayer();
});

watch(() => [props.showInterconnectors], () => {
    toggleInterconnectorLayers();
});

watch(() => props.showPlants, () => {
    setPlantLayers();
});

</script>

<style lang="scss" scoped>
.map-wrapper {
    height: 100%;
    position: relative;
    width: 100%;

    .hint-text {
        background-color: var(--neutral0);
        border-radius: var(--borderRadiusOuter);
        font-size: var(--extraSmallFontSize);
        opacity: 0.9;
        padding: 5px;
        position: absolute;
        right: 5px;
        top: 5px;
    }

    .fullscreen-button {
        top: 90px;
        position: absolute !important;
        left: 11px;
        z-index: 1;
        border-radius: var(--borderRadiusOuter);
        border: 1px solid var(--neutral400);
        background-color: white;
    }

    .map-base {
        height: 100%;
        width: 100%;
        position: relative;
        z-index: 0;

        :deep(.map-icon) {
            border-radius: 50%;
            border: 1px solid #fff;
            height: 100%;
            opacity: 0.8;
            width: 100%;

            &.inter {
                border: none;
                opacity: 0.5;
            }
        }

        :deep(.leaflet-div-icon) {
            background-color: transparent;
            border: none;
        }

        :deep(.leaflet-tooltip) {
            font-size: var(--defaultFontSize);
        }
    }
}

.locked-screen {
    height: 100%;
    position: relative;

    .central {
        display: flex;
        flex-direction: column;
        gap: 20px;
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
    }

    .image-fit {
        height: 100%;
        opacity: 0.3;
        width: 100%;
    }

    .lock-icon {
        display: table;
        font-size: 7rem;
        margin: 0 auto;
    }

    .required-text {
        color: var(--primaryFont);
        font-weight: bolder;
        text-align: center;
    }
}

</style>
